import i18n from '../i18n';
import { getAmplifyVisitorRegion, type VisitorRegion } from './amplifyGeo';

export const ANDROID_DOWNLOAD_ROUTE = '/android-download/';

export const ANDROID_DIRECT_APK_DOWNLOAD_LINK = 'https://www.jlbhk.com/apk/metatrader.apk';

// Keep this alias for older imports/components, if any.
export const ANDROID_CHINA_DOWNLOAD_LINK = ANDROID_DIRECT_APK_DOWNLOAD_LINK;

export const MT5_ANDROID_PACKAGE_ID = 'net.metaquotes.metatrader5';
export const GOOGLE_PLAY_PACKAGE_ID = 'com.android.vending';

/**
 * Original Android global / Google Play / MetaQuotes download link.
 * Other regions should go here directly without showing the special HK/CN/US handling path.
 */
export const ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK =
  'https://download.terminal.free/cdn/mobile/mt5/android?hl=en&utm_source=www.metatrader5.com&utm_campaign=install.metaquotes';

// Keep the old exported name for existing imports/components.
export const ANDROID_GLOBAL_DOWNLOAD_LINK = ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK;

/**
 * Android Chrome intent link kept for optional manual use only.
 * Do not use it as an AWS 302 target because desktop and some in-app browsers may fail with
 * "scheme does not have a registered handler".
 */
export const ANDROID_PLAY_STORE_INTENT_WITH_APK_FALLBACK_LINK =
  `intent://details?id=${MT5_ANDROID_PACKAGE_ID}` +
  `#Intent;scheme=market;package=${GOOGLE_PLAY_PACKAGE_ID};` +
  `S.browser_fallback_url=${encodeURIComponent(ANDROID_DIRECT_APK_DOWNLOAD_LINK)};end`;

const ANDROID_SPECIAL_CHOICE_COUNTRY_CODES = new Set(['CN', 'HK', 'US']);

function normalizeLanguage(language: string) {
  return language.trim().toLowerCase().replace('_', '-');
}

function normalizeCountryCode(countryCode: string | null | undefined) {
  return countryCode?.trim().toUpperCase() || '';
}

export function isAndroidBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

export function shouldShowAndroidDownloadChoice(countryCode: string | null | undefined) {
  return ANDROID_SPECIAL_CHOICE_COUNTRY_CODES.has(normalizeCountryCode(countryCode));
}

// Backward-compatible name. HK/CN/US stay on the site for regional handling instead of auto-downloading.
export const shouldUseDirectAndroidApk = shouldShowAndroidDownloadChoice;

export function getAndroidGlobalDownloadLink() {
  return ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK;
}

export function getAndroidDownloadLinkByCountryCode(countryCode: string | null | undefined) {
  return shouldShowAndroidDownloadChoice(countryCode)
    ? ANDROID_DOWNLOAD_ROUTE
    : getAndroidGlobalDownloadLink();
}

export function getAndroidDownloadLinkByRegion(region: Pick<VisitorRegion, 'countryCode'>) {
  return getAndroidDownloadLinkByCountryCode(region.countryCode);
}

export function isLikelyDirectApkRegionByLanguage() {
  const languageCandidates = new Set<string>();

  if (i18n.language) {
    languageCandidates.add(i18n.language);
  }

  if (typeof navigator !== 'undefined') {
    if (navigator.language) {
      languageCandidates.add(navigator.language);
    }

    (navigator.languages || []).forEach((language) => {
      if (language) {
        languageCandidates.add(language);
      }
    });
  }

  return Array.from(languageCandidates).some((language) => {
    const normalized = normalizeLanguage(language);
    return (
      normalized === 'cn' ||
      normalized === 'hk' ||
      normalized === 'zh-cn' ||
      normalized.startsWith('zh-cn-') ||
      normalized === 'zh-hans' ||
      normalized.startsWith('zh-hans-') ||
      normalized === 'zh-hk' ||
      normalized.startsWith('zh-hk-')
    );
  });
}

export function getAndroidDownloadLinkByLanguage() {
  return isLikelyDirectApkRegionByLanguage() ? ANDROID_DOWNLOAD_ROUTE : getAndroidGlobalDownloadLink();
}

export async function getAndroidDownloadLink() {
  const region = await getAmplifyVisitorRegion();
  return getAndroidDownloadLinkByRegion(region);
}

export function getAndroidDownloadRouteUrl() {
  if (typeof window === 'undefined') {
    return ANDROID_DOWNLOAD_ROUTE;
  }

  return new URL(ANDROID_DOWNLOAD_ROUTE, window.location.origin).toString();
}
